import React, { useState } from 'react'
import { X, Upload, FileText, AlertTriangle, CheckCircle, Ship, Link2 } from 'lucide-react'
import {
  projectsApi,
  ParseRaDocumentResult,
  ProjectComplianceProfile,
  RaBucket,
} from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { Button } from '@/components/ui/Button'

interface Props {
  projectId: string
  complianceProfiles: ProjectComplianceProfile[]
  onClose: () => void
  onCommitted: () => void
}

// Only classes with a controlled project profile/template set are offered.
// RS/CCS are added when the first corresponding project package is available;
// never route them through another class as a fallback.
const CLASS_SOCIETIES = ['ABS', 'BV', 'DNV', 'KR', 'LR', 'NK', 'RINA'] as const

const BUCKET_LABELS: Record<RaBucket, string> = {
  target:     'Target systems',
  exclusion:  'Excluded CBSs',
  non_target: 'Non-target systems',
  ip_based:   'IP-based comm. interfaces',
  pending:    'Pending / unassigned',
}

export const ImportRaWordModal: React.FC<Props> = ({
  projectId,
  complianceProfiles,
  onClose,
  onCommitted,
}) => {
  const { error: showError, success } = useToast()
  const [file, setFile] = useState<File | null>(null)
  const [parseResult, setParseResult] = useState<ParseRaDocumentResult | null>(null)
  const [parsing, setParsing] = useState(false)
  const [committing, setCommitting] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [classSocietyOverride, setClassSocietyOverride] = useState<string>('')
  const [warningsExpanded, setWarningsExpanded] = useState(false)
  // upload progress 0..100 from the chunked upload helper. 100 +
  // parsing=true means the bytes are on the server and the parser is
  // running, typically <1s for a 33MB workbook
  const [progress, setProgress] = useState(0)
  // match overrides keyed by ra_system_index → project_system_id | null.
  // seeded from the server's match_suggestions (confidence='auto' → suggested
  // ps id, else null). the manual-map UI is gone; the seed still rides through
  // to commit so auto-matches land. remap/corrections live on the Mappings tab
  const [matchOverrides, setMatchOverrides] = useState<Record<string, string | null>>({})
  const allowedClassSocieties = complianceProfiles.length > 0
    ? complianceProfiles.map(profile => profile.class_society)
    : [...CLASS_SOCIETIES]

  const handleFileChange = async (f: File | null) => {
    setFile(f)
    setParseResult(null)
    setMatchOverrides({})
    setProgress(0)
    if (!f) return
    if (!f.name.toLowerCase().endsWith('.docx')) {
      showError('File must be a .docx Word document')
      setFile(null)
      return
    }
    setParsing(true)
    try {
      const result = await projectsApi.parseRaDocument(projectId, f, setProgress)
      setParseResult(result)
      const suggested = String(result.suggested_class_society ?? '').toUpperCase()
      setClassSocietyOverride(
        allowedClassSocieties.includes(suggested as typeof allowedClassSocieties[number])
          ? suggested
          : allowedClassSocieties.length === 1
            ? allowedClassSocieties[0]
            : '',
      )
      // seed overrides: auto-matched rows get their suggested ps id,
      // review / none rows get explicit null so the dropdown shows
      // "(no match)" instead of reading from the absent map entry
      const seed: Record<string, string | null> = {}
      for (const s of result.match_suggestions) {
        seed[String(s.ra_system_index)] = s.confidence === 'auto' ? s.project_system_id : null
      }
      setMatchOverrides(seed)
    } catch (err: any) {
      showError(err.message ?? 'Failed to parse document')
      setFile(null)
    } finally {
      setParsing(false)
    }
  }

  const handleCommit = async () => {
    if (!parseResult || !file) return
    if (!classSocietyOverride) {
      showError('Select which assigned class this Risk Assessment belongs to')
      return
    }
    setCommitting(true)
    try {
      const result = await projectsApi.commitRaDocument(projectId, {
        parsed:           parseResult.parsed,
        class_society:    classSocietyOverride || null,
        source_filename:  file.name,
        session_id:       parseResult.session_id,
        match_overrides:  matchOverrides,
      })
      const extras = [
        `${result.master_row_count} master rows`,
        `${result.detail_count} detailed sections`,
        result.diagrams_count > 0 ? `${result.diagrams_count} diagrams` : null,
        result.match_resolved_count > 0 ? `${result.match_resolved_count} mapped` : null,
        result.unmatched_detail_count > 0 ? `${result.unmatched_detail_count} unmatched details` : null,
      ].filter(Boolean)
      success(`Word RA imported (${extras.join(' · ')})`)
      onCommitted()
    } catch (err: any) {
      showError(err.message ?? 'Failed to import')
    } finally {
      setCommitting(false)
    }
  }

  // bucket-aware counts for the preview
  const bucketCounts = React.useMemo(() => {
    if (!parseResult) return null
    const c: Record<RaBucket, number> = { target: 0, exclusion: 0, non_target: 0, ip_based: 0, pending: 0 }
    for (const row of parseResult.parsed.master_table) c[row.bucket]++
    return c
  }, [parseResult])

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold">Import Word Risk Assessment</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!parseResult && !parsing && (
            <div
              onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setDragActive(true) }}
              onDragOver={e  => { e.preventDefault(); e.stopPropagation(); setDragActive(true) }}
              onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragActive(false) }}
              onDrop={e => {
                e.preventDefault(); e.stopPropagation(); setDragActive(false)
                const f = e.dataTransfer.files?.[0]
                if (f) handleFileChange(f)
              }}
              className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
                dragActive
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-slate-300 dark:border-slate-600 hover:border-blue-400'
              }`}
              onClick={() => document.getElementById('ra-word-file-input')?.click()}
            >
              <Upload className={`h-9 w-9 mx-auto mb-3 ${dragActive ? 'text-blue-500' : 'text-slate-400'}`} />
              <div className="text-sm font-medium">
                {file ? file.name : dragActive ? 'Drop the file here' : 'Drop a Word RA workbook here or click to browse'}
              </div>
              <div className="text-xs text-slate-400 mt-1">.docx file (class society Risk Assessment). Uploaded in 1 MB chunks; max 100 MB.</div>
              <input
                id="ra-word-file-input"
                type="file"
                accept=".docx"
                className="hidden"
                onChange={e => handleFileChange(e.target.files?.[0] ?? null)}
              />
            </div>
          )}

          {parsing && (
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-6 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <Upload className="h-4 w-4 text-blue-500 flex-shrink-0" />
                  <span className="font-medium truncate">{file?.name ?? 'Uploading…'}</span>
                </div>
                <span className="text-slate-500 tabular-nums">{progress}%</span>
              </div>
              <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-150"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {progress < 100
                  ? `Uploading in 1 MB chunks${file ? ` (${(file.size / (1024 * 1024)).toFixed(1)} MB total)` : ''}…`
                  : 'Parsing on server…'}
              </div>
            </div>
          )}

          {parseResult && bucketCounts && (
            <>
              {/* Detected header */}
              <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-slate-500" />
                  <div className="text-xs uppercase tracking-wide text-slate-400">Detected from document</div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Field label="Vessel name"       value={parseResult.parsed.header.vessel_name} />
                  <Field label="Project"           value={parseResult.parsed.header.project_name} />
                  <Field label="Class society"     value={parseResult.parsed.header.class_society} />
                  <Field label="Revision"          value={parseResult.parsed.header.revision} />
                </div>
                {parseResult.parsed.header.applicable_rules.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Applicable rules</div>
                    <ul className="list-disc list-inside text-xs text-slate-600 dark:text-slate-300 space-y-0.5">
                      {parseResult.parsed.header.applicable_rules.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
              </div>

              {/* Bucket counts */}
              <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Ship className="h-4 w-4 text-slate-500" />
                  <div className="text-xs uppercase tracking-wide text-slate-400">Master table preview</div>
                </div>
                <div className="space-y-1">
                  {(Object.entries(bucketCounts) as Array<[RaBucket, number]>).map(([bucket, n]) => (
                    <div key={bucket} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-300">{BUCKET_LABELS[bucket]}</span>
                      <span className="text-slate-900 dark:text-white font-medium tabular-nums">{n}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2 mt-2 border-t border-slate-200 dark:border-slate-700 text-sm font-medium">
                    <span>Total master rows</span>
                    <span className="tabular-nums">{parseResult.parsed.master_table.length}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-300">Per-system detailed sections extracted</span>
                    <span className="text-slate-900 dark:text-white font-medium tabular-nums">{parseResult.parsed.excluded_details.length}</span>
                  </div>
                </div>
              </div>

              {/* Class society chooser */}
              <div className="p-3 border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10 rounded-lg">
                <label className="flex items-center gap-3">
                  <CheckCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span className="text-sm font-medium">Class society</span>
                  <select
                    value={classSocietyOverride}
                    onChange={e => setClassSocietyOverride(e.target.value)}
                    className="ml-auto text-sm border border-slate-300 dark:border-slate-600 dark:bg-slate-800 rounded px-2 py-1"
                  >
                    <option value="">Select assigned class…</option>
                    {allowedClassSocieties.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 ml-7">
                  Auto-detected: <strong>{parseResult.suggested_class_society ?? '(none)'}</strong>.
                  {' '}Only classes assigned to this vessel can be imported.
                </div>
              </div>

              {/* auto-map summary. matching runs on import (buildMatchSuggestions
                  → seeded into matchOverrides → commit). review/remap on the
                  project's Mappings tab afterward */}
              <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg">
                <div className="flex items-center gap-2">
                  <Link2 className="h-4 w-4 text-slate-500" />
                  <div className="text-xs uppercase tracking-wide text-slate-400">System matching</div>
                  <span className="ml-auto text-[11px] text-slate-500">
                    Auto-mapped{' '}
                    <strong className="text-emerald-600 dark:text-emerald-400">
                      {parseResult.match_suggestions.filter(s => s.confidence === 'auto').length}
                    </strong>
                    {' of '}{parseResult.match_suggestions.length}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 ml-6">
                  Systems are auto-mapped on import. Review or remap on the project's Mappings tab.
                </div>
              </div>

              {/* Parse warnings */}
              {parseResult.parsed.parse_warnings.length > 0 && (
                <div className="p-3 border border-amber-200 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-900/10 rounded-lg">
                  <button
                    onClick={() => setWarningsExpanded(x => !x)}
                    className="w-full flex items-center gap-2 text-left"
                  >
                    <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    <span className="text-sm font-medium">
                      {parseResult.parsed.parse_warnings.length} parse warning{parseResult.parsed.parse_warnings.length !== 1 ? 's' : ''}
                    </span>
                    <span className="ml-auto text-xs text-slate-500">{warningsExpanded ? 'hide' : 'show'}</span>
                  </button>
                  {warningsExpanded && (
                    <ul className="mt-2 ml-6 list-disc text-xs text-slate-600 dark:text-slate-300 space-y-0.5">
                      {parseResult.parsed.parse_warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <Button variant="secondary" onClick={onClose} disabled={committing}>Cancel</Button>
          {parseResult && (
            <Button onClick={handleCommit} disabled={committing || !classSocietyOverride}>
              {committing ? 'Importing…' : 'Import Word RA'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

const Field: React.FC<{ label: string; value: string | null }> = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className="text-sm text-slate-700 dark:text-slate-200 mt-0.5">
      {value ?? <span className="text-slate-300 dark:text-slate-600 italic">—</span>}
    </div>
  </div>
)

